import { alpha, Card, CardActionArea, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import PosterTile from "../../components/PosterTile";
import {
  // getContentLabel,
  getContentOptions,
  getStatusOptions,
} from "../../utils/utils";
import { useAuth } from "../../context/authContext";
import { useStatusSummary } from "../../hooks/useCustomQuery";
import { useThemeContext } from "../../context/themeContext";
import { buildListPath } from "../../utils/detailPaths";

// % of the strip width each poster tile occupies — matches what a full 5-poster
// stack already resolves to today, so those cards render pixel-identically.
const POSTER_TILE_WIDTH = 28;
// Cap on positive spacing (tiles pulled apart rather than overlapping) so a
// 1-2 poster stack doesn't fan out absurdly wide.
const MAX_POSTER_GAP = 8;

/**
 * Compact collection card for Explore carousels.
 * Fixed width so it can live inside a horizontal scroll container.
 * onClick: called with the full item when tapped → opens the collection dialog.
 * The card is also a real link to the collection's URL, so crawlers can follow
 * it and modified clicks (cmd/ctrl/shift) open it in a new tab as usual; only a
 * plain click is intercepted to open the dialog in-app instead. On touch
 * devices the native long-press link menu/preview is suppressed, so the card
 * behaves like an app tile rather than offering to open outside the PWA.
 *
 * finishedCount: viewer's finished-item total from the backend (across ALL
 * items, not just the visible posters). null → no authenticated viewer.
 *
 * When the user opens this collection this session, the full list is in the
 * React Query cache and all statuses are prefetched. In that case we swap to a
 * live count derived from the cache so status changes made inside the dialog
 * are reflected on the card immediately, even without a route change.
 *
 * Its hover/tap scale comes from the Carousel it sits in (see itemScale).
 */
export default function CollectionCarouselCard({
  list_id,
  title,
  itemCount,
  contentType,
  finishedCount,
  posters,
  onClick,
  titleLines = 1,
}) {
  const { user } = useAuth();
  const { isTouchDevice } = useThemeContext();
  const contentOptions = getContentOptions();
  const statusOptions = getStatusOptions();

  // Observe the cached full list without triggering a fetch. Populated as a
  // side effect when the user opens the collection dialog.
  const { data: cachedList } = useQuery({
    queryKey: ["list", list_id],
    enabled: false,
  });

  // Extract content IDs from the cached list (empty when not yet opened).
  // useMemo avoids recreating the array on every render when cachedList is stable.
  const cachedContentIds = useMemo(
    () => cachedList?.items?.map((i) => i.content_id) ?? [],
    [cachedList],
  );

  // Always call unconditionally (hooks rules). Degenerates to a no-op when
  // cachedContentIds is empty — usePrefetchContentStatuses early-returns and
  // the cache subscriber fires but never matches.
  const { finishedCount: liveFinishedCount, isLoading: liveIsLoading } =
    useStatusSummary(cachedContentIds);

  // Use the live count when: logged in, list has been opened (cache exists),
  // and useStatusSummary has completed its initial compute. Otherwise fall back
  // to the backend value so the card always shows something meaningful.
  const effectiveFinishedCount =
    user && cachedList && !liveIsLoading ? liveFinishedCount : finishedCount;
  const effectiveItemCount =
    user && cachedList ? cachedList.items.length : itemCount;

  // Compute percentage only when we have a real count and a non-empty list;
  // otherwise show an em-dash placeholder (logged-out or zero-item edge case).
  const hasFinishedData =
    effectiveFinishedCount !== null &&
    effectiveFinishedCount !== undefined &&
    effectiveItemCount > 0;
  const finishedPct = hasFinishedData
    ? Math.round((effectiveFinishedCount / effectiveItemCount) * 100)
    : null;

  // Tiles are a fixed % of the strip, so the card is the same height regardless
  // of poster count; overlap flexes to keep the stack spanning the full width.
  // n tiles + (n-1) gaps == 100%  →  gap = (100 - width*n) / (n - 1)
  const posterGap =
    posters.length > 1
      ? Math.min(
          (100 - POSTER_TILE_WIDTH * posters.length) / (posters.length - 1),
          MAX_POSTER_GAP,
        )
      : 0;

  return (
    <Card
      elevation={1}
      sx={{
        width: { xs: "65vw", sm: "300px" },
        flexShrink: 0,
        scrollSnapAlign: "start",
        bgcolor: (t) => alpha(t.palette.secondary.main, 0.5),
        // MUI's dark-mode Paper overlay would otherwise lighten this on top of
        // the custom bgcolor above — keep the elevation shadow, drop the tint.
        backgroundImage: "none",
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={buildListPath({ list_id, title })}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          onClick?.();
        }}
        onContextMenu={isTouchDevice ? (e) => e.preventDefault() : undefined}
        sx={{
          p: 1,
          ...(isTouchDevice && { WebkitTouchCallout: "none" }),
          // The Carousel already scales the card on hover/tap; suppress MUI's
          // default darkening overlay so that's the only hover cue.
          "&:hover .MuiCardActionArea-focusHighlight": { opacity: 0 },
        }}
      >
        <Stack spacing={1}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: "bold",
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: titleLines,
              // Fixed height when titleLines > 1 keeps cards uniform across the carousel
              ...(titleLines > 1 && { height: `${titleLines}lh` }),
            }}
          >
            {title}
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              {contentOptions[contentType].icon}
              <Typography variant="body2">
                {itemCount}{" "}
                {/* {getContentLabel({
                  type: contentType,
                  lowercase: true,
                  plural: itemCount !== 1,
                })} */}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {finishedPct === 100
                ? statusOptions["finished"].iconFilled
                : statusOptions["finished"].icon}
              <Typography variant="body2">
                {finishedPct === null ? "—" : finishedPct}% finished
              </Typography>
            </Stack>
          </Stack>

          {/* Poster strip. Tile width is fixed regardless of poster count so
              every card is the same height in the carousel; posterGap (above)
              flexes the overlap/spread to keep the stack spanning full width. */}
          <Stack
            direction="row"
            spacing={`${posterGap}%`}
            justifyContent="center"
            sx={{
              "& > *": {
                flexBasis: `${POSTER_TILE_WIDTH}%`,
                maxWidth: `${POSTER_TILE_WIDTH}%`,
                flexShrink: 0,
              },
            }}
          >
            {posters.map((url, index) => (
              <PosterTile
                key={index}
                contentId={null}
                contentType={contentType}
                contentTitle=""
                imageUrl={url}
                padding={0.5}
                zIndex={5 - index}
              />
            ))}
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}
